import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom'; // ← added useNavigate
import { Icon } from '@iconify/react';
import { useLanguage } from '../../i18n';
import styles from './Homepage.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const categoryMapping = {
  'digital-fundamentals': {
    title: "Digital Security",
    description: "Protect devices, communications, and online identity",
    icon: "lucide:shield",
    color: "var(--color-info-light)",
    link: "/collections/digital-fundamentals"
  },
  'legal-aid': {
    title: "Legal Aid & Rights",
    description: "Emergency legal support, documentation, advocacy tools",
    icon: "lucide:scale",
    color: "var(--color-success-light)",
    link: "/collections/legal-aid"
  },
  'mental-health': {
    title: "Mental Health & Wellbeing",
    description: "Crisis hotlines, trauma resources, peer support",
    icon: "lucide:heart-pulse",
    color: "var(--color-warning-light)",
    link: "/support?type=mental-health"
  },
  'emergency-protocols': {
    title: "Emergency Protocols",
    description: "Rapid response, evacuation, incident reporting",
    icon: "lucide:alert-triangle",
    color: "var(--color-error-light)",
    link: "/collections/emergency-protocols"
  },
  // Add more mappings as you create new collections in DB
};

export default function Homepage() {
  const { t } = useLanguage();
  const [featuredCollections, setFeaturedCollections] = useState([]);
  const [quickCategories, setQuickCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(''); // ← new state for search input
  const navigate = useNavigate(); // ← for redirecting to search results

  useEffect(() => {
    const fetchData = async () => {
      console.log('API_URL:', API_URL); // Debugging line to check API URL
      try {
        const res = await fetch(`${API_URL}/collections`);
        console.log('Fetch response:', res); // Debugging line to check response
        if (!res.ok) throw new Error('Failed to fetch collections');
        const collections = await res.json();

        // Featured collections: filter isFeatured=true (limit to 2 for homepage)
        const featured = collections
          .filter(c => c.isFeatured)
          .slice(0, 2)
          .map(c => ({
            id: c.id,
            title: c.title,
            count: c.resources?.length || 0,
            desc: c.tags?.join(' • ') || 'Various topics'
          }));
        setFeaturedCollections(featured);

        // Quick categories: map relevant collections to your fixed cards
        const mappedCategories = Object.entries(categoryMapping)
          .map(([slug, frontendCat]) => {
            const backendCol = collections.find(c => c.slug === slug);
            return backendCol
              ? { ...frontendCat, count: backendCol.resources?.length || 0 }
              : frontendCat;
          });
        setQuickCategories(mappedCategories);

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle search submit (button click or Enter key)
  const handleSearch = (e) => {
    e.preventDefault();
    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) return; // prevent empty search

    // Navigate to search results page with query in URL
    navigate(`/search?q=${encodeURIComponent(trimmedQuery)}`);
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Icon icon="lucide:loader-2" className={styles.spinner} />
        Loading resources...
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <Icon icon="lucide:alert-triangle" />
        {error}
      </div>
    );
  }

  return (
    <>
      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <h1 className={styles.heroTitle}>
          {t('homepageHeroTitle')}
          </h1>
          <p className={styles.heroSubtitle}>
            {t('homepageHeroSubtitle')}
          </p>
          {/* <p className={styles.heroSubtitle}>
              Engage. Empower. Connect
          </p> */}

          {/* Search – now working */}
          <form onSubmit={handleSearch} className={styles.searchContainer}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('searchPlaceholder')}
              className={styles.searchInput}
            />
            <button type="submit" className={styles.searchBtn}>
              <Icon icon="lucide:search" />
            </button>
          </form>

          <div className={styles.popular}>
          <span>{t('popular')}</span>
            {['Digital Security', 'Legal Aid', 'Mental Health', 'Emergency Contacts'].map((term) => (
              <button
                key={term}
                className={styles.popularTag}
                onClick={() => {
                  setSearchQuery(term);
                  navigate(`/search?q=${encodeURIComponent(term)}`);
                }}
              >
                {term}
              </button>
            ))}
          </div>

          <div className={styles.crisisBanner}>
            <Icon icon="lucide:alert-triangle" className={styles.crisisIcon} />
          <strong>{t('inImmediateDanger')}</strong>
            <Link to="/support" className={styles.crisisLink}>
            {t('getHelpNow')} →
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Access Categories */}
      <section className={styles.categories}>
        <h2 className={styles.sectionTitle}>Quick Access to Critical Resources</h2>
        <div className={styles.categoriesGrid}>
          {quickCategories.map((cat) => (
            <Link to={cat.link} key={cat.title} className={styles.categoryCard}>
              <div className={styles.categoryIcon} style={{ backgroundColor: cat.color }}>
                <Icon icon={cat.icon} />
              </div>
              <h3>{cat.title}</h3>
              <p>{cat.description}</p>
              {cat.count > 0 && <small>({cat.count} resources)</small>}
              <span className={styles.categoryArrow}>Explore →</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Collections */}
      <section className={styles.featured}>
        <h2 className={styles.sectionTitle}>Featured Collections</h2>
        <div className={styles.featuredGrid}>
          {featuredCollections.map((item) => (
            <div key={item.id} className={styles.featuredCard}>
              <div className={styles.featuredPlaceholder}></div>
              <h4>{item.title}</h4>
              <p>{item.count} resources • {item.desc}</p>
            </div>
          ))}
          {featuredCollections.length === 0 && (
            <p>No featured collections available yet.</p>
          )}
        </div>
        <Link to="/collections" className={styles.viewAll}>
          View All Collections →
        </Link>
      </section>
    </>
  );
}
// import { useState, useEffect } from 'react';
// import { Link } from 'react-router-dom';
// import { Icon } from '@iconify/react';
// import styles from './Homepage.module.css';

// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// console.log('API_URL:', API_URL); // Debugging line to check API URL
// // Map backend collection categories to frontend quick-access cards
// // (you can expand this mapping as you add more collections in DB)
// const categoryMapping = {
//   'digital-fundamentals': {
//     title: "Digital Security",
//     description: "Protect devices, communications, and online identity",
//     icon: "lucide:shield",
//     color: "var(--color-info-light)",
//     link: "/collections/digital-fundamentals"
//   },
//   'legal-aid': {
//     title: "Legal Aid & Rights",
//     description: "Emergency legal support, documentation, advocacy tools",
//     icon: "lucide:scale",
//     color: "var(--color-success-light)",
//     link: "/collections/legal-aid"
//   },
//   'mental-health': {
//     title: "Mental Health & Wellbeing",
//     description: "Crisis hotlines, trauma resources, peer support",
//     icon: "lucide:heart-pulse",
//     color: "var(--color-warning-light)",
//     link: "/support?type=mental-health"
//   },
//   'emergency-protocols': {
//     title: "Emergency Protocols",
//     description: "Rapid response, evacuation, incident reporting",
//     icon: "lucide:alert-triangle",
//     color: "var(--color-error-light)",
//     link: "/collections/emergency-protocols"
//   },
//   // Add more mappings as you create new collections in DB
// };

// export default function Homepage() {
//   console.log('API_URL:', API_URL); // Debugging line to check API URL
  
//   const [featuredCollections, setFeaturedCollections] = useState([]);
//   const [quickCategories, setQuickCategories] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         // Fetch all collections from backend
//         const res = await fetch(`${API_URL}/collections`);
//         if (!res.ok) throw new Error('Failed to fetch collections');
//         const collections = await res.json();

//         // Featured collections: filter isFeatured=true (limit to 2 for homepage)
//         const featured = collections
//           .filter(c => c.isFeatured)
//           .slice(0, 2)
//           .map(c => ({
//             id: c.id,
//             title: c.title,
//             count: c.resources?.length || 0,
//             desc: c.tags?.join(' • ') || 'Various topics'
//           }));
//         setFeaturedCollections(featured);

//         // Quick categories: map relevant collections to your fixed cards
//         const mappedCategories = Object.entries(categoryMapping)
//           .map(([slug, frontendCat]) => {
//             const backendCol = collections.find(c => c.slug === slug);
//             return backendCol
//               ? { ...frontendCat, count: backendCol.resources?.length || 0 }
//               : frontendCat; // fallback to static if no match in DB
//           });
//         setQuickCategories(mappedCategories);

//       } catch (err) {
//         setError(err.message);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, []);

//   if (loading) {
//     return (
//       <div className={styles.loading}>
//         <Icon icon="lucide:loader-2" className={styles.spinner} />
//         Loading resources...
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className={styles.error}>
//         <Icon icon="lucide:alert-triangle" />
//         {error}
//       </div>
//     );
//   }

//   return (
//     <>
//       {/* Hero – unchanged */}
//       <section className={styles.hero}>
//         <div className={styles.heroInner}>
//           <h1 className={styles.heroTitle}>
//             Critical Safety Resources When You Need Them Most
//           </h1>
//           <p className={styles.heroSubtitle}>
//             Comprehensive safeguarding resources and support networks for activists, CSOs, and human rights organizations worldwide.
//           </p>

//           <div className={styles.searchContainer}>
//             <input
//               type="text"
//               placeholder="Search for safety resources, legal aid, digital security guides..."
//               className={styles.searchInput}
//             />
//             <button className={styles.searchBtn}>
//               <Icon icon="lucide:search" />
//             </button>
//           </div>

//           <div className={styles.popular}>
//             <span>Popular:</span>
//             {['Digital Security', 'Legal Aid', 'Mental Health', 'Emergency Contacts'].map((term) => (
//               <button key={term} className={styles.popularTag}>{term}</button>
//             ))}
//           </div>

//           <div className={styles.crisisBanner}>
//             <Icon icon="lucide:alert-triangle" className={styles.crisisIcon} />
//             <strong>In immediate danger?</strong>
//             <Link to="/support" className={styles.crisisLink}>
//               Access Crisis Resources →
//             </Link>
//           </div>
//         </div>
//       </section>

//       {/* Quick Access Categories – now dynamic */}
//       <section className={styles.categories}>
//         <h2 className={styles.sectionTitle}>Quick Access to Critical Resources</h2>
//         <div className={styles.categoriesGrid}>
//           {quickCategories.map((cat) => (
//             <Link to={cat.link} key={cat.title} className={styles.categoryCard}>
//               <div className={styles.categoryIcon} style={{ backgroundColor: cat.color }}>
//                 <Icon icon={cat.icon} />
//               </div>
//               <h3>{cat.title}</h3>
//               <p>{cat.description}</p>
//               {cat.count > 0 && <small>({cat.count} resources)</small>}
//               <span className={styles.categoryArrow}>Explore →</span>
//             </Link>
//           ))}
//         </div>
//       </section>

//       {/* Featured Collections – now dynamic */}
//       <section className={styles.featured}>
//         <h2 className={styles.sectionTitle}>Featured Collections</h2>
//         <div className={styles.featuredGrid}>
//           {featuredCollections.map((item) => (
//             <div key={item.id} className={styles.featuredCard}>
//               <div className={styles.featuredPlaceholder}></div>
//               <h4>{item.title}</h4>
//               <p>{item.count} resources • {item.desc}</p>
//             </div>
//           ))}
//           {featuredCollections.length === 0 && (
//             <p>No featured collections available yet.</p>
//           )}
//         </div>
//         <Link to="/collections" className={styles.viewAll}>
//           View All Collections →
//         </Link>
//       </section>
//     </>
//   );
// }
// // src/pages/Homepage/Homepage.jsx
// import { Link } from 'react-router-dom';
// import { Icon } from '@iconify/react';
// import styles from './Homepage.module.css';

// const dummyCategories = [
//   {
//     id: 1,
//     title: "Digital Security",
//     description: "Protect devices, communications, and online identity",
//     icon: "lucide:shield",
//     color: "var(--color-info-light)",
//     link: "/collections?category=digital-security"
//   },
//   {
//     id: 2,
//     title: "Legal Aid & Rights",
//     description: "Emergency legal support, documentation, advocacy tools",
//     icon: "lucide:scale",
//     color: "var(--color-success-light)",
//     link: "/collections?category=legal"
//   },
//   {
//     id: 3,
//     title: "Mental Health & Wellbeing",
//     description: "Crisis hotlines, trauma resources, peer support",
//     icon: "lucide:heart-pulse",
//     color: "var(--color-warning-light)",
//     link: "/support?type=mental-health"
//   },
//   {
//     id: 4,
//     title: "Emergency Protocols",
//     description: "Rapid response, evacuation, incident reporting",
//     icon: "lucide:alert-triangle",
//     color: "var(--color-error-light)",
//     link: "/collections?category=emergency"
//   },
//   {
//     id: 5,
//     title: "Community Networks",
//     description: "Regional solidarity groups, trusted contacts",
//     icon: "lucide:users",
//     color: "#e9d5ff",
//     link: "/support?type=community"
//   },
//   {
//     id: 6,
//     title: "Resource Collections",
//     description: "Curated bundles by theme and region",
//     icon: "lucide:folder",
//     color: "#fed7aa",
//     link: "/collections"
//   }
// ];

// const dummyFeatured = [
//   { id: 1, title: "Essential Safeguarding Toolkit 2026", count: 248, desc: "Digital • Legal • Mental Health • Emergency" },
//   { id: 2, title: "High-Risk Activist Survival Guide", count: 87, desc: "Device hardening, threat modeling, secure comms" }
// ];

// export default function Homepage() {
//   return (
//     <>
//       {/* Hero */}
//       <section className={styles.hero}>
//         <div className={styles.heroInner}>
//           <h1 className={styles.heroTitle}>
//             Critical Safety Resources When You Need Them Most
//           </h1>
//           <p className={styles.heroSubtitle}>
//             Comprehensive safeguarding resources and support networks for activists, CSOs, and human rights organizations worldwide.
//           </p>

//           <div className={styles.searchContainer}>
//             <input
//               type="text"
//               placeholder="Search for safety resources, legal aid, digital security guides..."
//               className={styles.searchInput}
//             />
//             <button className={styles.searchBtn}>
//               <Icon icon="lucide:search" />
//             </button>
//           </div>

//           <div className={styles.popular}>
//             <span>Popular:</span>
//             {['Digital Security', 'Legal Aid', 'Mental Health', 'Emergency Contacts'].map((term) => (
//               <button key={term} className={styles.popularTag}>{term}</button>
//             ))}
//           </div>

//           <div className={styles.crisisBanner}>
//             <Icon icon="lucide:alert-triangle" className={styles.crisisIcon} />
//             <strong>In immediate danger?</strong>
//             <Link to="/support" className={styles.crisisLink}>
//               Access Crisis Resources →
//             </Link>
//           </div>
//         </div>
//       </section>

//       {/* Categories */}
//       <section className={styles.categories}>
//         <h2 className={styles.sectionTitle}>Quick Access to Critical Resources</h2>
//         <div className={styles.categoriesGrid}>
//           {dummyCategories.map((cat) => (
//             <Link to={cat.link} key={cat.id} className={styles.categoryCard}>
//               <div className={styles.categoryIcon} style={{ backgroundColor: cat.color }}>
//                 <Icon icon={cat.icon} />
//               </div>
//               <h3>{cat.title}</h3>
//               <p>{cat.description}</p>
//               <span className={styles.categoryArrow}>Explore →</span>
//             </Link>
//           ))}
//         </div>
//       </section>

//       {/* Featured */}
//       <section className={styles.featured}>
//         <h2 className={styles.sectionTitle}>Featured Collections</h2>
//         <div className={styles.featuredGrid}>
//           {dummyFeatured.map((item) => (
//             <div key={item.id} className={styles.featuredCard}>
//               <div className={styles.featuredPlaceholder}></div>
//               <h4>{item.title}</h4>
//               <p>{item.count} resources • {item.desc}</p>
//             </div>
//           ))}
//         </div>
//         <Link to="/collections" className={styles.viewAll}>
//           View All Collections →
//         </Link>
//       </section>
//     </>
//   );
// }
// import { Icon } from '@iconify/react';
// import styles from './Homepage.module.css';

// export default function Homepage() {
//   return (
//     <div className={styles.pageWrapper}>
//       {/* Hero Section */}
//       <section className={styles.hero}>
//         <div className={styles.heroContent}>
//           <h1 className={styles.heroTitle}>
//             Critical Safety Resources When You Need Them Most
//           </h1>

//           <p className={styles.heroSubtitle}>
//             Comprehensive safeguarding resources and support networks for activists, CSOs, and human rights organizations worldwide.
//           </p>

//           <div className={styles.searchContainer}>
//             <input
//               type="text"
//               placeholder="Search for safety resources, legal aid, digital security guides..."
//               className={styles.searchInput}
//             />
//             <button className={styles.searchButton}>
//               <Icon icon="lucide:search" />
//             </button>
//           </div>

//           <div className={styles.popularSearches}>
//             <span>Popular searches:</span>
//             <button>Digital Security</button>
//             <button>Legal Aid</button>
//             <button>Mental Health</button>
//             <button>Emergency Contacts</button>
//           </div>

//           <div className={styles.crisisAlert}>
//             <Icon icon="lucide:alert-triangle" className={styles.alertIcon} />
//             <strong>In immediate danger?</strong>
//             <a href="#" className={styles.alertLink}>
//               Access Crisis Resources →
//             </a>
//           </div>
//         </div>
//       </section>

//       {/* Categories / Quick Access */}
//       <section className={styles.categoriesSection}>
//         <h2 className={styles.sectionTitle}>Quick Access to Critical Resources</h2>

//         <div className={styles.categoriesGrid}>
//           <div className={styles.categoryCard}>
//             <div className={styles.categoryIcon} style={{ backgroundColor: 'var(--color-info-light)' }}>
//               <Icon icon="lucide:shield" />
//             </div>
//             <h3>Digital Security</h3>
//             <p>Protect devices, communications, and online identity</p>
//             <a href="#" className={styles.categoryLink}>Explore →</a>
//           </div>

//           <div className={styles.categoryCard}>
//             <div className={styles.categoryIcon} style={{ backgroundColor: 'var(--color-success-light)' }}>
//               <Icon icon="lucide:scale" />
//             </div>
//             <h3>Legal Aid & Rights</h3>
//             <p>Emergency legal support, documentation, advocacy tools</p>
//             <a href="#" className={styles.categoryLink}>Explore →</a>
//           </div>

//           <div className={styles.categoryCard}>
//             <div className={styles.categoryIcon} style={{ backgroundColor: 'var(--color-warning-light)' }}>
//               <Icon icon="lucide:heart-pulse" />
//             </div>
//             <h3>Mental Health & Wellbeing</h3>
//             <p>Crisis hotlines, trauma resources, peer support</p>
//             <a href="#" className={styles.categoryLink}>Explore →</a>
//           </div>

//           <div className={styles.categoryCard}>
//             <div className={styles.categoryIcon} style={{ backgroundColor: 'var(--color-error-light)' }}>
//               <Icon icon="lucide:alert-triangle" />
//             </div>
//             <h3>Emergency Protocols</h3>
//             <p>Rapid response, evacuation, incident reporting</p>
//             <a href="#" className={styles.categoryLink}>Explore →</a>
//           </div>

//           <div className={styles.categoryCard}>
//             <div className={styles.categoryIcon} style={{ backgroundColor: '#e9d5ff' }}>
//               <Icon icon="lucide:users" />
//             </div>
//             <h3>Community Networks</h3>
//             <p>Regional solidarity groups, trusted contacts</p>
//             <a href="#" className={styles.categoryLink}>Explore →</a>
//           </div>

//           <div className={styles.categoryCard}>
//             <div className={styles.categoryIcon} style={{ backgroundColor: '#fed7aa' }}>
//               <Icon icon="lucide:folder" />
//             </div>
//             <h3>Resource Collections</h3>
//             <p>Curated bundles by theme and region</p>
//             <a href="#" className={styles.categoryLink}>Explore →</a>
//           </div>
//         </div>
//       </section>

//       {/* Featured Collections Teaser */}
//       <section className={styles.featuredSection}>
//         <h2 className={styles.sectionTitle}>Featured Collections</h2>
//         <div className={styles.featuredGrid}>
//           <div className={styles.featuredItem}>
//             <div className={styles.featuredPlaceholder}></div>
//             <h4>Essential Safeguarding Toolkit 2026</h4>
//             <p>248 resources • Digital, legal, mental health, emergency</p>
//           </div>
//           <div className={styles.featuredItem}>
//             <div className={styles.featuredPlaceholder}></div>
//             <h4>High-Risk Activist Survival Guide</h4>
//             <p>Device hardening, threat modeling, secure comms</p>
//           </div>
//         </div>
//         <a href="#" className={styles.viewAllLink}>
//           View All Collections →
//         </a>
//       </section>

//       {/* Footer */}
//       <footer className={styles.footer}>
//         <div className={styles.footerContainer}>
//           <div className={styles.footerTop}>
//             {/* Left - Logo & Description */}
//             <div className={styles.footerBrand}>
//               <div className={styles.footerLogo}>
//                 <Icon icon="fa-solid:shield-alt" />
//               </div>
//               <span className={styles.footerName}>SafeGuard Resource Hub</span>
//               <p className={styles.footerTagline}>
//                 Built for activists, by activists
//               </p>
//             </div>

//             {/* Middle - Links */}
//             <div className={styles.footerLinks}>
//               <div className={styles.linkColumn}>
//                 <h4>Resources</h4>
//                 <a href="#">Browse All</a>
//                 <a href="#">Digital Security</a>
//                 <a href="#">Legal Aid</a>
//                 <a href="#">Collections</a>
//               </div>

//               <div className={styles.linkColumn}>
//                 <h4>Support</h4>
//                 <a href="#">Help Center</a>
//                 <a href="#">Community Forum</a>
//                 <a href="#">Submit Resource</a>
//                 <a href="#">Report Issue</a>
//               </div>

//               <div className={styles.linkColumn}>
//                 <h4>Account</h4>
//                 <a href="#">My Resources</a>
//                 <a href="#">Account Settings</a>
//                 <a href="#">Sign Out</a>
//               </div>
//             </div>

//             {/* Right - Emergency Support */}
//             <div className={styles.emergencyBox}>
//               <h4>Emergency Support</h4>
//               <div className={styles.hotlineCard}>
//                 <div className={styles.hotlineIcon}>
//                   <Icon icon="lucide:phone" />
//                 </div>
//                 <div>
//                   <strong>Crisis Hotline</strong>
//                   <p>24/7 emergency support available</p>
//                 </div>
//               </div>
//               <button className={styles.emergencyBtn}>
//                 Get Help Now
//               </button>
//             </div>
//           </div>

//           {/* Bottom Bar */}
//           <div className={styles.footerBottom}>
//             <div className={styles.copyright}>
//               <Icon icon="fa-solid:shield-alt" className={styles.smallShield} />
//               © 2026 SafeGuard Resource Hub. All rights reserved.
//             </div>
//             <div className={styles.footerMeta}>
//               <span>Built with care for human rights defenders</span>
//             </div>
//           </div>
//         </div>
//       </footer>
//     </div>
//   );
// }

// import { Icon } from '@iconify/react';
// import styles from './Homepage.module.css';

// export default function Homepage() {
//   return (
//     <>
//       {/* Hero Section */}
//       <section className={styles.hero}>
//         <div className={styles.heroInner}>
//           <h1 className={styles.heroTitle}>
//             Critical Safety Resources When You Need Them Most
//           </h1>

//           <p className={styles.heroSubtitle}>
//             Comprehensive safeguarding resources and support networks for activists, CSOs, and human rights organizations worldwide.
//           </p>

//           <div className={styles.searchContainer}>
//             <input
//               type="text"
//               placeholder="Search for safety resources, legal aid, digital security guides..."
//               className={styles.searchInput}
//             />
//             <button className={styles.searchButton}>
//               <Icon icon="lucide:search" />
//             </button>
//           </div>

//           <div className={styles.popularSearches}>
//             <span>Popular:</span>
//             <button>Digital Security</button>
//             <button>Legal Aid</button>
//             <button>Mental Health</button>
//             <button>Emergency Contacts</button>
//           </div>

//           <div className={styles.crisisAlert}>
//             <Icon icon="lucide:alert-triangle" className={styles.alertIcon} />
//             <strong>In immediate danger?</strong>
//             <a href="#" className={styles.alertLink}>
//               Access Crisis Resources →
//             </a>
//           </div>
//         </div>
//       </section>

//       {/* Quick Access Cards */}
//       <section className={styles.quickAccess}>
//         <h2 className={styles.sectionTitle}>Quick Access to Critical Resources</h2>

//         <div className={styles.cardsGrid}>
//           <div className={styles.card}>
//             <div className={styles.cardIcon}>
//               <Icon icon="lucide:shield" />
//             </div>
//             <h3>Digital Security</h3>
//             <p>Protect your communications, devices, and online presence with trusted guides.</p>
//             <a href="#" className={styles.cardLink}>
//               Access Guides →
//             </a>
//           </div>

//           <div className={styles.card}>
//             <div className={styles.cardIcon} style={{ backgroundColor: 'var(--color-success-light)' }}>
//               <Icon icon="lucide:scale" />
//             </div>
//             <h3>Legal Aid</h3>
//             <p>Find pro-bono lawyers, emergency legal support, and rights documentation.</p>
//             <a href="#" className={styles.cardLink}>
//               View Directory →
//             </a>
//           </div>

//           <div className={styles.card}>
//             <div className={styles.cardIcon} style={{ backgroundColor: 'var(--color-warning-light)' }}>
//               <Icon icon="lucide:heart-pulse" />
//             </div>
//             <h3>Mental Health Support</h3>
//             <p>Confidential helplines, trauma resources, and peer support networks.</p>
//             <a href="#" className={styles.cardLink}>
//               Find Help →
//             </a>
//           </div>

//           <div className={styles.card}>
//             <div className={styles.cardIcon} style={{ backgroundColor: 'var(--color-info-light)' }}>
//               <Icon icon="lucide:users" />
//             </div>
//             <h3>Community Networks</h3>
//             <p>Connect with trusted regional networks and solidarity groups.</p>
//             <a href="#" className={styles.cardLink}>
//               Explore Networks →
//             </a>
//           </div>
//         </div>
//       </section>

//       {/* Featured Resources / Collections (optional next section) */}
//       <section className={styles.featured}>
//         <h2 className={styles.sectionTitle}>Featured Collections</h2>
//         {/* You can add 3–4 horizontal or grid cards here */}
//       </section>
//     </>
//   );
// }